import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';
import nebulaTheme from '@nebula/theme';
import GlobalStyles from '@nebula/global-styles';
import { push } from 'connected-react-router';
import { useDispatch } from 'react-redux';
import { CANCELLATION_STAGES, CANCELLATION_TYPES } from './constants';
import CoolingOffPeriodStage from './stages/CoolingOffPeriodStage/index';
import ConfirmCancelStage from './stages/ConfirmCancelStage/index';
import ResultStage from './stages/ResultStage/index';
import { trackKeepSubscriptionClick } from '../../helpers/switch/analytics';
import cancelSubscriptionTransition, { CancelSubscriptionStep } from '../../helpers/cancelSubscriptionTransition';
import ErrorStage from './stages/ErrorStage';

const CoolingOffStageRenderer = ({
  currentStage,
  subscriptionFamily,
  nextBillingDate,
  isDeferred,
  isImmediate,
  isFlexFlow,
  goToCoolingOff,
  goToConfirmCancel,
  goToResult,
  setDeferred,
  setImmediate,
  onBack,
  onKeepSubscription,
  onDeferredCancel,
  onImmediateCancel,
  onBackHome,
  isWebview,
  goToErrorPage,
}) => {
  const handleBackFromConfirm = () => {
    goToCoolingOff();
  };

  switch (currentStage) {
    case CANCELLATION_STAGES.COOLING_OFF:
      return (
        <CoolingOffPeriodStage
          onBack={onBack}
          onKeepSubscription={onKeepSubscription}
          isWebview={isWebview}
          isFlexFlow={isFlexFlow}
          subscriptionFamily={subscriptionFamily}
          nextBillingDate={nextBillingDate}
          goToConfirmCancel={goToConfirmCancel}
          setDeferred={setDeferred}
          setImmediate={setImmediate}
        />
      );

    case CANCELLATION_STAGES.CONFIRM_CANCEL:
      return (
        <ConfirmCancelStage
          onBack={handleBackFromConfirm}
          onKeepSubscription={onKeepSubscription}
          onDeferredCancel={onDeferredCancel}
          onImmediateCancel={onImmediateCancel}
          isWebview={isWebview}
          isFlexFlow={isFlexFlow}
          subscriptionFamily={subscriptionFamily}
          nextBillingDate={nextBillingDate}
          isDeferred={isDeferred}
          isImmediate={isImmediate}
          goToResult={goToResult}
          goToErrorPage={goToErrorPage}
        />
      );

    case CANCELLATION_STAGES.RESULT:
      return (
        <ResultStage
          onBackHome={onBackHome}
          subscriptionFamily={subscriptionFamily}
          nextBillingDate={nextBillingDate}
          isFlexFlow={isFlexFlow}
          isDeferred={isDeferred}
          isImmediate={isImmediate}
        />
      );

    case CANCELLATION_STAGES.ERROR:
      return (
        <ErrorStage
          onTryAgain={goToConfirmCancel}
          subscriptionFamily={subscriptionFamily}
          isFlexFlow={isFlexFlow}
          isDeferred={isDeferred}
          isImmediate={isImmediate}
        />
      );

    default:
      return null;
  }
};

CoolingOffStageRenderer.propTypes = {
  currentStage: PropTypes.string.isRequired,
  subscriptionFamily: PropTypes.string.isRequired,
  nextBillingDate: PropTypes.string,
  isDeferred: PropTypes.bool.isRequired,
  isImmediate: PropTypes.bool.isRequired,
  isFlexFlow: PropTypes.bool,
  goToCoolingOff: PropTypes.func.isRequired,
  goToConfirmCancel: PropTypes.func.isRequired,
  goToResult: PropTypes.func.isRequired,
  setDeferred: PropTypes.func.isRequired,
  setImmediate: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onKeepSubscription: PropTypes.func.isRequired,
  onDeferredCancel: PropTypes.func,
  onImmediateCancel: PropTypes.func,
  onBackHome: PropTypes.func.isRequired,
  isWebview: PropTypes.bool,
  goToErrorPage: PropTypes.func.isRequired,
};

CoolingOffStageRenderer.defaultProps = {
  nextBillingDate: null,
  onDeferredCancel: null,
  onImmediateCancel: null,
  isWebview: false,
  isFlexFlow: false,
};

export default function CancelSubscriptionCoolingOffPeriod({
  subscriptionFamily,
  nextBillingDate,
  onBack,
  onDeferredCancel,
  onImmediateCancel,
  onBackHome,
  isWebview,
  isFlexFlow,
}) {
  const dispatch = useDispatch();
  const [currentStage, setCurrentStage] = useState(CANCELLATION_STAGES.COOLING_OFF);

  const [cancellationType, setCancellationType] = useState('');

  const goToCoolingOff = () => setCurrentStage(CANCELLATION_STAGES.COOLING_OFF);
  const goToConfirmCancel = () => setCurrentStage(CANCELLATION_STAGES.CONFIRM_CANCEL);
  const goToResult = () => setCurrentStage(CANCELLATION_STAGES.RESULT);
  const goToErrorPage = () => setCurrentStage(CANCELLATION_STAGES.ERROR);

  const setDeferred = () => setCancellationType(CANCELLATION_TYPES.DEFERRED);
  const setImmediate = () => setCancellationType(CANCELLATION_TYPES.IMMEDIATE);

  const isDeferred = cancellationType === CANCELLATION_TYPES.DEFERRED;
  const isImmediate = cancellationType === CANCELLATION_TYPES.IMMEDIATE;

  const keepSubscription = step => {
    trackKeepSubscriptionClick(subscriptionFamily, step);

    if (isWebview) {
      cancelSubscriptionTransition({ step: CancelSubscriptionStep.BACK });
      return;
    }

    dispatch(push('/'));
  };

  return (
    <ThemeProvider theme={nebulaTheme}>
      <GlobalStyles />
      <CoolingOffStageRenderer
        currentStage={currentStage}
        subscriptionFamily={subscriptionFamily}
        nextBillingDate={nextBillingDate}
        cancellationType={cancellationType}
        isDeferred={isDeferred}
        isImmediate={isImmediate}
        isFlexFlow={isFlexFlow}
        goToCoolingOff={goToCoolingOff}
        goToConfirmCancel={goToConfirmCancel}
        goToResult={goToResult}
        setDeferred={setDeferred}
        setImmediate={setImmediate}
        onBack={onBack}
        onKeepSubscription={keepSubscription}
        onDeferredCancel={onDeferredCancel}
        onImmediateCancel={onImmediateCancel}
        onBackHome={onBackHome}
        isWebview={isWebview}
        goToErrorPage={goToErrorPage}
      />
    </ThemeProvider>
  );
}

CancelSubscriptionCoolingOffPeriod.propTypes = {
  subscriptionFamily: PropTypes.string.isRequired,
  nextBillingDate: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  onDeferredCancel: PropTypes.func,
  onImmediateCancel: PropTypes.func,
  onBackHome: PropTypes.func.isRequired,
  isWebview: PropTypes.bool,
  isFlexFlow: PropTypes.bool,
};

CancelSubscriptionCoolingOffPeriod.defaultProps = {
  nextBillingDate: null,
  onDeferredCancel: null,
  onImmediateCancel: null,
  isWebview: false,
  isFlexFlow: false,
};
